import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronUp, Phone, MessageCircle, Mail, Calendar } from 'lucide-react';
import { Logo } from './Logo';
import { ROUTES } from '../constants';

interface Section {
  id: string;
  title: string;
}

interface PolicyLayoutProps {
  title: string;
  lastUpdated: string;
  sections: Section[];
  children: React.ReactNode;
}

export const PolicyLayout: React.FC<PolicyLayoutProps> = ({
  title,
  lastUpdated,
  sections,
  children,
}) => {
  const [activeSection, setActiveSection] = useState(sections[0]?.id || '');
  const [tocOpen, setTocOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const activeSectionIndex = sections.findIndex((s) => s.id === activeSection);

  const handleScroll = useCallback(() => {
    setShowScrollTop(window.scrollY > 400);

    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0;
    setScrollProgress(Math.min(progress, 100));

    const offsets = sections.map((s) => {
      const el = document.getElementById(s.id);
      return { id: s.id, top: el ? el.getBoundingClientRect().top : Infinity };
    });

    const current = offsets.reduce(
      (closest, item) => {
        if (item.top <= 120 && item.top > closest.top) return item;
        return closest;
      },
      { id: sections[0]?.id || '', top: -Infinity }
    );

    if (current.id) setActiveSection(current.id);
  }, [sections]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (tocOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [tocOpen]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTocOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-[3px] bg-gray-200/50">
        <div
          className="h-full bg-gradient-to-r from-teal-500 to-teal-600 transition-[width] duration-150 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={ROUTES.HOME} className="flex-shrink-0">
              <Logo size="sm" />
            </Link>
            <nav className="hidden sm:flex items-center gap-6 text-sm text-gray-600">
              <Link to={ROUTES.HOME} className="hover:text-gray-900 transition-colors">
                Home
              </Link>
              <Link to={ROUTES.LOGIN} className="hover:text-gray-900 transition-colors">
                Log In
              </Link>
              <Link
                to={ROUTES.REGISTER}
                className="text-teal-700 font-medium hover:text-teal-800 transition-colors"
              >
                Sign Up
              </Link>
            </nav>
            <Link
              to={ROUTES.HOME}
              className="sm:hidden text-sm text-gray-600 hover:text-gray-900"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-10">
          {/* Desktop sidebar with numbered TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-5">
                Contents
              </h3>
              <nav className="relative">
                {/* Vertical connecting line */}
                <div className="absolute left-[15px] top-3 bottom-3 w-px bg-gray-200" />
                {/* Progress fill */}
                <div
                  className="absolute left-[15px] top-3 w-px bg-teal-400 transition-all duration-300"
                  style={{
                    height:
                      activeSectionIndex >= 0
                        ? `${(activeSectionIndex / Math.max(sections.length - 1, 1)) * 100}%`
                        : '0%',
                    maxHeight: 'calc(100% - 24px)',
                  }}
                />

                <div className="space-y-0.5">
                  {sections.map((section, index) => {
                    const isActive = activeSection === section.id;
                    const isPast = index <= activeSectionIndex;
                    return (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`flex items-center gap-3 w-full text-left pl-1 pr-3 py-2 rounded-lg text-sm transition-all duration-200 group ${
                          isActive
                            ? 'bg-teal-50 text-teal-800 font-medium'
                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className={`relative z-10 flex-shrink-0 w-[30px] h-[30px] rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-200 ${
                            isActive
                              ? 'bg-teal-600 text-white shadow-sm shadow-teal-200'
                              : isPast
                              ? 'bg-teal-100 text-teal-700 border border-teal-200'
                              : 'bg-white text-gray-400 border border-gray-200 group-hover:border-gray-300 group-hover:text-gray-600'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="truncate">{section.title}</span>
                      </button>
                    );
                  })}
                </div>
              </nav>
            </div>
          </aside>

          {/* Mobile TOC button */}
          <div className="lg:hidden mb-6">
            <button
              onClick={() => setTocOpen(true)}
              className="flex items-center gap-2 w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
            >
              <Menu className="h-4 w-4" />
              Table of Contents
              <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {sections.length} sections
              </span>
            </button>
          </div>

          {/* Mobile TOC drawer overlay */}
          {tocOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div
                className="absolute inset-0 bg-black/40 animate-fade-in"
                onClick={() => setTocOpen(false)}
              />
              <div className="absolute left-0 top-0 bottom-0 w-[300px] max-w-[85vw] bg-white shadow-2xl animate-slide-in-left overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Contents</h3>
                  <button
                    onClick={() => setTocOpen(false)}
                    className="p-1 rounded-md hover:bg-gray-100 text-gray-400"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <nav className="p-3 space-y-0.5">
                  {sections.map((section, index) => {
                    const isActive = activeSection === section.id;
                    return (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                          isActive
                            ? 'bg-teal-50 text-teal-800 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                            isActive
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {index + 1}
                        </span>
                        {section.title}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          )}

          <main ref={contentRef}>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Enhanced hero banner */}
              <div className="relative px-6 sm:px-8 lg:px-10 py-10 sm:py-12 border-b border-gray-100 overflow-hidden">
                {/* Subtle dot pattern background */}
                <div
                  className="absolute inset-0 opacity-[0.03]"
                  style={{
                    backgroundImage:
                      'radial-gradient(circle, #0d9488 1px, transparent 1px)',
                    backgroundSize: '20px 20px',
                  }}
                />
                {/* Gradient accent bar on left */}
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-teal-500 via-teal-600 to-teal-700" />

                <div className="relative">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 text-xs font-medium text-teal-700 mb-4">
                    <Calendar className="w-3 h-3" />
                    Last updated: {lastUpdated}
                  </div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                    {title}
                  </h1>
                  <div className="mt-4 w-16 h-1 rounded-full bg-gradient-to-r from-teal-500 to-teal-300" />
                </div>
              </div>

              {/* Content area with enhanced prose styling */}
              <div
                className="px-6 sm:px-8 lg:px-10 py-8 sm:py-10 prose prose-gray max-w-none
                prose-headings:scroll-mt-24
                prose-h2:text-xl prose-h2:sm:text-2xl prose-h2:font-bold prose-h2:text-gray-900 prose-h2:mt-12 prose-h2:mb-6 prose-h2:pl-4 prose-h2:border-l-4 prose-h2:border-teal-500 prose-h2:leading-snug
                prose-h3:text-lg prose-h3:font-semibold prose-h3:text-gray-800 prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-600 prose-p:leading-[1.85] prose-p:mb-5
                prose-li:text-gray-600 prose-li:leading-[1.85] prose-li:mb-2
                prose-ul:mb-6 prose-ul:mt-3 prose-ol:mb-6 prose-ol:mt-3
                prose-strong:text-gray-800
                prose-a:text-teal-700 prose-a:no-underline hover:prose-a:underline
                prose-table:border-collapse prose-table:w-full prose-table:rounded-lg prose-table:overflow-hidden
                prose-th:bg-gray-50 prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:text-sm prose-th:font-semibold prose-th:text-gray-700 prose-th:border prose-th:border-gray-200
                prose-td:px-4 prose-td:py-3 prose-td:text-sm prose-td:border prose-td:border-gray-200
              "
              >
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>

      <footer className="bg-gray-900 text-gray-300 py-10 sm:py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 sm:gap-10 lg:gap-6 mb-8 sm:mb-10">
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="mb-3 [&_span]:!text-white">
                <Logo size="sm" />
              </div>
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                Connecting families with quality care providers across the UK.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-4">Legal</h4>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link
                    to={ROUTES.TERMS_OF_SERVICE}
                    className="hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTES.PRIVACY_POLICY}
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTES.COOKIE_POLICY}
                    className="hover:text-white transition-colors"
                  >
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTES.CANCELLATION_POLICY}
                    className="hover:text-white transition-colors"
                  >
                    Cancellation Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-4">Support</h4>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link
                    to={ROUTES.SAFEGUARDING_POLICY}
                    className="hover:text-white transition-colors"
                  >
                    Safeguarding Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTES.COMPLAINTS_PROCEDURE}
                    className="hover:text-white transition-colors"
                  >
                    Complaints Procedure
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTES.ACCESSIBILITY}
                    className="hover:text-white transition-colors"
                  >
                    Accessibility
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-4">Contact</h4>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <a
                    href="mailto:Info@nursingcarebooking.com"
                    className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    <span className="whitespace-nowrap">Info@nursingcarebooking.com</span>
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+447462365503"
                    className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>+44 7462 365503</span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://wa.me/447462365503"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 hover:text-green-400 transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-gray-800 text-sm text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} NursingCareBooking.com. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-50 p-3 bg-teal-700 text-white rounded-full shadow-lg hover:bg-teal-800 transition-all duration-300 hover:scale-105"
          aria-label="Scroll to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};
